import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { learningDevelopmentApi } from '../learningDevelopmentApi';
import type {
  LearningDevelopmentCatalog,
  LearningDevelopmentEntry,
} from '../learningDevelopmentTypes';
import { sampleLearningDevelopmentEntry } from '../utils/sampleLearningDevelopmentEntry';

/**
 * Create or edit one row of any of the four L&D vocabularies.
 *
 * One modal for all four, parameterised by the catalog descriptor — they are one shape, and the
 * only thing that differs between them is the words. See `LearningDevelopmentCatalog`.
 *
 * No position field: where a row sits in its vocabulary is assigned by the server and changed by
 * moving the row, not by typing a number here — a rename is not a reorder.
 */
export function LearningDevelopmentModal({
  catalog,
  open,
  editing,
  onClose,
  onSaved,
}: {
  catalog: LearningDevelopmentCatalog;
  open: boolean;
  /** Null creates. */
  editing: LearningDevelopmentEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
    };

    try {
      if (editing) await learningDevelopmentApi.update(catalog.slug, editing.id, request);
      else await learningDevelopmentApi.create(catalog.slug, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError
          ? caught
          : new ApiError(`Could not save this ${catalog.noun}.`, 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ld-entry' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleLearningDevelopmentEntry(catalog.slug);

    setName(sample.name);
    setDescription(sample.description);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Names the row on an edit, so a reader who opened the wrong one sees it before they type.
      title={editing ? `Edit ${editing.name}` : `New ${catalog.noun}`}
      size="2xl"
      id="ld-entry-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'ld-entry-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ld-entry-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ld-entry-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="ld-entry-name"
            label={catalog.columnHeader}
            required
            description={catalog.fieldDescription}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : `Enter the ${catalog.noun}'s name.`}
            disabled={saving}
          />

          <TextField
            id="ld-entry-description"
            label="Description"
            description="What this covers. Read here, on this page — not by the clerk filling in a 201 file."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={error?.fieldError('description')}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
